import React from 'react';
import { Dialog, DialogProps } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';

import {
  SidebarDialogProvider,
  useSidebarDialog,
} from './SidebarDialogContext';
import { SidebarDialogProps } from './SidebarDialogTypes';

export const SidebarDialog: React.FC<SidebarDialogProps> = ({
  children,
  // Extract Dialog props
  open,
  onClose,
  title,
  subtitle,
  leftAdornment,
  actions,
  ...dialogProps
}) => {
  // Detect if there's a FAB child
  const childrenArray = React.Children.toArray(children);
  const hasFab = childrenArray.some(
    (child) =>
      React.isValidElement(child) &&
      typeof child.type === 'function' &&
      (child.type as any).displayName === 'SidebarDialogFab'
  );

  return (
    <SidebarDialogProvider
      open={open}
      onClose={onClose}
      hasFab={hasFab}
      dialogTitle={title}
      dialogSubtitle={subtitle}
      dialogLeftAdornment={leftAdornment}
      dialogActions={actions as React.ReactNode}
    >
      <DialogWrapper
        open={open}
        title={title}
        subtitle={subtitle}
        leftAdornment={leftAdornment}
        actions={actions}
        {...dialogProps}
      >
        <SidebarDialogAutoLayout>{children}</SidebarDialogAutoLayout>
      </DialogWrapper>
    </SidebarDialogProvider>
  );
};

// Wrapper that handles Dialog props based on responsive state
const DialogWrapper: React.FC<DialogProps> = ({ children, ...dialogProps }) => {
  const {
    isMobile,
    sidebarOpen,
    dialogTitle,
    dialogSubtitle,
    dialogLeftAdornment,
    dialogActions,
    onClose,
  } = useSidebarDialog();

  // Check if we have a sidebar showing (desktop with sidebar open)
  const hasSidebar = !isMobile && sidebarOpen;

  return (
    <Dialog
      {...dialogProps}
      fullScreen={isMobile}
      fullWidth
      // When no sidebar, pass props to Dialog; when sidebar, let content handle them
      actions={!hasSidebar ? dialogActions : undefined}
      spacing={!hasSidebar ? 2 : 0}
      title={!hasSidebar ? dialogTitle : undefined}
      subtitle={!hasSidebar ? dialogSubtitle : undefined}
      leftAdornment={!hasSidebar ? dialogLeftAdornment : undefined}
      onClose={isMobile ? onClose : undefined}
    >
      {children}
    </Dialog>
  );
};

// Internal layout component that handles responsive layout automatically
const SidebarDialogAutoLayout: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { isMobile, sidebarOpen } = useSidebarDialog();

  // Check if we have a sidebar showing (desktop with sidebar open)
  const hasSidebar = !isMobile && sidebarOpen;

  if (hasSidebar) {
    return (
      <Stack
        direction="row"
        sx={{
          alignItems: 'stretch',
          maxHeight: 'calc(100vh - 64px)',
          minHeight: '80vh',
        }}
      >
        {children}
      </Stack>
    );
  }

  // For non-sidebar mode, just return children - padding is handled by content component
  return <>{children}</>;
};
